/**
 * Renders all projected (surface-locked) scene sketch strokes as camera-space
 * Line2 ribbons.
 *
 * Strokes live at scene level (`AppState.sketch.layers`), and each projected
 * point carries its own host object attachment. The overlay resolves those
 * point hosts against the live viewport meshes each frame, allowing one
 * stroke to cross multiple objects.
 *
 * Each frame the overlay CPU-skins every stroke point (using baked
 * skinIndex/skinWeight for SkinnedMesh targets, or plain `mesh.matrixWorld`
 * for static meshes) into world coordinates, writes them into a
 * `LineSegmentsGeometry` as duplicated segment pairs, and renders with
 * `Line2NodeMaterial` in world-units mode.
 *
 * Capacity growth allocates a fresh `LineSegmentsGeometry` (WebGPU
 * resource discipline; see geometry-swap comment below).
 */

import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineSegments2 } from "three/examples/jsm/lines/webgpu/LineSegments2.js";
import * as THREE from "three/webgpu";
import { useStore } from "../app/store.ts";
import {
  isProjectedStroke,
  type SketchStroke,
  type ProjectedStroke,
} from "../modeling/sketch.ts";
import { projectedStrokeWorldPositions } from "../sketch/sceneSketch.ts";
import { setFatLinePositions } from "./objectHelperLines.ts";
import { screenToWorldRadius } from "./screenRadius.ts";
import { buildSketchMeshIndex } from "./sketchTargets.ts";

const STROKE_POLYGON_OFFSET_FACTOR = -1;
const STROKE_POLYGON_OFFSET_UNITS = -4;
const STROKE_SURFACE_LIFT_PX = 1.5;
const SKINNED_STROKE_SURFACE_LIFT_PX = 4;
const LEGACY_STROKE_WORLD_THICKNESS_PER_PX = 0.001;

interface StrokeRenderState {
  geo: LineSegmentsGeometry;
  mat: THREE.Line2NodeMaterial;
  line: LineSegments2;
  worldBuf: Float32Array;
  segBuf: Float32Array;
  capacity: number;
  lastColor: string;
  lastThickness: number;
  lastCompositeOpacity: number;
}

function nextCapacity(required: number, current: number): number {
  let cap = Math.max(current, 64);
  while (cap < required) cap *= 2;
  return cap;
}

function strokeWorldThickness(stroke: SketchStroke): number {
  const worldThickness = stroke.worldThickness;
  if (worldThickness != null && Number.isFinite(worldThickness)) {
    return Math.max(1e-5, worldThickness);
  }
  return Math.max(
    1e-5,
    stroke.thickness * LEGACY_STROKE_WORLD_THICKNESS_PER_PX,
  );
}

function swapStrokeGeometry(rs: StrokeRenderState): void {
  // Capacity growth creates a fresh LineSegmentsGeometry identity — WebGPU
  // can keep the old instanced vertex buffer bound after instanceStart /
  // instanceEnd are replaced on a rendered geometry; reusing the geometry
  // freezes the viewport mid-stroke at every 2x growth boundary.
  const oldGeo = rs.geo;
  const nextGeo = new LineSegmentsGeometry();
  rs.geo = nextGeo;
  rs.line.geometry = nextGeo;
  oldGeo.dispose();
}

function fillSafeTail(
  segBuf: Float32Array,
  validSegments: number,
  capacity: number,
): void {
  if (validSegments >= capacity) return;
  const src = validSegments > 0 ? (validSegments - 1) * 6 + 3 : 0;
  const x = segBuf[src] ?? 0;
  const y = segBuf[src + 1] ?? 0;
  const z = segBuf[src + 2] ?? 0;
  const eps = 1e-5;
  for (let i = validSegments; i < capacity; i++) {
    const o = i * 6;
    segBuf[o] = x;
    segBuf[o + 1] = y;
    segBuf[o + 2] = z;
    segBuf[o + 3] = x + eps;
    segBuf[o + 4] = y;
    segBuf[o + 5] = z;
  }
}

function writeSegments(
  worldBuf: Float32Array,
  segBuf: Float32Array,
  pointCount: number,
): void {
  for (let i = 0; i < pointCount - 1; i++) {
    const a = i * 3;
    const b = (i + 1) * 3;
    const o = i * 6;
    segBuf[o] = worldBuf[a];
    segBuf[o + 1] = worldBuf[a + 1];
    segBuf[o + 2] = worldBuf[a + 2];
    segBuf[o + 3] = worldBuf[b];
    segBuf[o + 4] = worldBuf[b + 1];
    segBuf[o + 5] = worldBuf[b + 2];
  }
}

const _liftPoint = new THREE.Vector3();
const _liftDir = new THREE.Vector3();

function liftWorldBufferTowardCamera(
  worldBuf: Float32Array,
  pointCount: number,
  camera: THREE.Camera,
  viewportHeight: number,
  surfaceLiftPx: number,
): void {
  if (viewportHeight <= 0) return;

  const perspective = (camera as THREE.PerspectiveCamera).isPerspectiveCamera;
  if (!perspective) {
    camera.getWorldDirection(_liftDir).multiplyScalar(-1).normalize();
  }

  for (let i = 0; i < pointCount; i++) {
    const o = i * 3;
    _liftPoint.set(worldBuf[o], worldBuf[o + 1], worldBuf[o + 2]);
    const lift = screenToWorldRadius(
      surfaceLiftPx,
      _liftPoint,
      camera,
      viewportHeight,
    );
    if (!Number.isFinite(lift) || lift <= 0) continue;

    if (perspective) {
      _liftDir.copy(camera.position).sub(_liftPoint);
      const len = _liftDir.length();
      if (len <= 1e-8) continue;
      _liftDir.multiplyScalar(1 / len);
    }

    _liftPoint.addScaledVector(_liftDir, lift);
    worldBuf[o] = _liftPoint.x;
    worldBuf[o + 1] = _liftPoint.y;
    worldBuf[o + 2] = _liftPoint.z;
  }
}

function hasFiniteValues(buf: Float32Array, length: number): boolean {
  for (let i = 0; i < length; i++) {
    if (!Number.isFinite(buf[i])) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StrokeOverlay() {
  const sketch = useStore((s) => s.sketch);
  const sketchStrokeSeq = useStore((s) => s.sketchStrokeSeq);
  const { scene, invalidate, camera, size: viewportSize } = useThree();

  // Pointer-side mutations bump `sketchStrokeSeq` but don't otherwise touch
  // React state R3F watches; without an explicit invalidate the useFrame
  // stops firing the moment the user stops moving the cursor over another
  // subscriber, leaving new stroke samples un-drawn.
  useEffect(() => {
    invalidate();
  }, [sketchStrokeSeq, invalidate]);

  const rsMapRef = useRef(new Map<string, StrokeRenderState>());
  const rootRef = useRef<THREE.Group | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: sketchStrokeSeq is intentional — see comment below
  const visibleStrokes = useMemo(() => {
    const out: { stroke: ProjectedStroke; layerOpacity: number }[] = [];
    for (const layer of sketch.layers) {
      if (!layer.visible) continue;
      for (const stroke of layer.strokes) {
        if (!isProjectedStroke(stroke)) continue;
        out.push({ stroke, layerOpacity: layer.opacity });
      }
    }
    return out;
    // sketchStrokeSeq forces a re-enumeration when strokes mutate in place
    // during an active stroke (beginStroke/addStrokePoint bump the seq
    // without cloning the layers array).
  }, [sketch, sketchStrokeSeq]);

  useEffect(() => {
    const group = new THREE.Group();
    group.renderOrder = 5;
    group.userData.strokeOverlayRoot = true;
    group.userData.excludeFromExport = true;
    group.userData.sketchOverlay = true;
    rootRef.current = group;
    scene.add(group);
    return () => {
      scene.remove(group);
      rootRef.current = null;
    };
  }, [scene]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const rsMap = rsMapRef.current;
    const keep = new Set<string>();

    for (const { stroke, layerOpacity } of visibleStrokes) {
      keep.add(stroke.id);
      let rs = rsMap.get(stroke.id);
      const compositeOpacity = stroke.opacity * layerOpacity;
      const lineWidth = strokeWorldThickness(stroke);
      if (!rs) {
        const geo = new LineSegmentsGeometry();
        const mat = new THREE.Line2NodeMaterial({
          color: new THREE.Color(stroke.color).getHex(),
          linewidth: lineWidth,
          transparent: true,
          opacity: compositeOpacity,
          depthTest: true,
          depthWrite: false,
        });
        mat.worldUnits = true;
        mat.polygonOffset = true;
        mat.polygonOffsetFactor = STROKE_POLYGON_OFFSET_FACTOR;
        mat.polygonOffsetUnits = STROKE_POLYGON_OFFSET_UNITS;
        const line = new LineSegments2(geo, mat);
        line.frustumCulled = false;
        line.raycast = () => {};
        line.userData.excludeFromExport = true;
        line.userData.sketchOverlay = true;
        root.add(line);
        rs = {
          geo,
          mat,
          line,
          worldBuf: new Float32Array(0),
          segBuf: new Float32Array(0),
          capacity: 0,
          lastColor: stroke.color,
          lastThickness: lineWidth,
          lastCompositeOpacity: compositeOpacity,
        };
        rsMap.set(stroke.id, rs);
      } else {
        if (rs.lastColor !== stroke.color) {
          rs.mat.color.set(stroke.color);
          rs.lastColor = stroke.color;
        }
        if (rs.lastThickness !== lineWidth) {
          rs.mat.linewidth = lineWidth;
          rs.lastThickness = lineWidth;
        }
        if (rs.lastCompositeOpacity !== compositeOpacity) {
          rs.mat.opacity = compositeOpacity;
          rs.lastCompositeOpacity = compositeOpacity;
        }
      }
    }

    for (const [id, rs] of rsMap) {
      if (keep.has(id)) continue;
      root.remove(rs.line);
      rs.geo.dispose();
      rs.mat.dispose();
      rsMap.delete(id);
    }
  }, [visibleStrokes]);

  useEffect(() => {
    const rsMap = rsMapRef.current;
    return () => {
      for (const rs of rsMap.values()) {
        rs.geo.dispose();
        rs.mat.dispose();
      }
      rsMap.clear();
    };
  }, []);

  useFrame(() => {
    const root = rootRef.current;
    if (!root) return;
    root.visible = true;

    const state = useStore.getState();
    const meshByObject = buildSketchMeshIndex(scene, (objectId) =>
      state.sceneObjects.has(objectId),
    );
    const topologyVersionByObject = new Map<string, number>();
    for (const [objectId, obj] of state.sceneObjects) {
      topologyVersionByObject.set(objectId, obj.topologyVersion ?? 0);
    }

    for (const mesh of meshByObject.values()) {
      try {
        mesh.updateMatrixWorld();
        const skinnedMesh = (mesh as THREE.SkinnedMesh).isSkinnedMesh
          ? (mesh as THREE.SkinnedMesh)
          : null;
        if (skinnedMesh?.skeleton) skinnedMesh.skeleton.update();
      } catch {
        // A bad host mesh only invalidates points attached to it this frame.
      }
    }

    const rsMap = rsMapRef.current;
    for (const { stroke } of visibleStrokes) {
      const rs = rsMap.get(stroke.id);
      if (!rs) continue;

      const pointCount = stroke.points.length;
      if (pointCount < 2) {
        rs.line.visible = false;
        rs.geo.instanceCount = 0;
        continue;
      }
      rs.line.visible = true;

      const segments = pointCount - 1;
      if (segments > rs.capacity) {
        const newCap = nextCapacity(segments, rs.capacity);
        rs.worldBuf = new Float32Array((newCap + 1) * 3);
        rs.segBuf = new Float32Array(newCap * 6);
        rs.capacity = newCap;
        swapStrokeGeometry(rs);
      }

      try {
        const positions = projectedStrokeWorldPositions(
          stroke,
          (point) => {
            const currentVersion = topologyVersionByObject.get(
              point.attachment.objectId,
            );
            if (
              point.corners &&
              currentVersion !== undefined &&
              currentVersion !== point.attachment.topologyVersion
            ) {
              return null;
            }
            return meshByObject.get(point.attachment.objectId);
          },
          rs.worldBuf,
        );
        if (!positions) {
          rs.line.visible = false;
          continue;
        }
        let surfaceLiftPx = STROKE_SURFACE_LIFT_PX;
        for (const point of stroke.points) {
          const host = meshByObject.get(point.attachment.objectId);
          if ((host as THREE.SkinnedMesh | undefined)?.isSkinnedMesh) {
            surfaceLiftPx = SKINNED_STROKE_SURFACE_LIFT_PX;
            break;
          }
        }
        liftWorldBufferTowardCamera(
          rs.worldBuf,
          pointCount,
          camera,
          viewportSize.height,
          surfaceLiftPx,
        );
      } catch {
        rs.line.visible = false;
        continue;
      }

      writeSegments(rs.worldBuf, rs.segBuf, pointCount);
      fillSafeTail(rs.segBuf, segments, rs.capacity);

      if (!hasFiniteValues(rs.segBuf, rs.capacity * 6)) continue;

      try {
        setFatLinePositions(rs.geo, rs.segBuf, null, false);
        rs.geo.instanceCount = segments;
      } catch {
        // Swallow upload failure for the frame; next frame retries.
      }
    }
  });

  return null;
}
